import { type StepperRef, useIntl, type UserAMLQuestionnaireRequirementConfig } from '@capsicum/common';
import { Stepper, useInfoModal } from '@capsicum/common-web';
import { useAmlQuestionnaire } from '@capsicum/features/regulations/shared';
import { type Requirement } from '@capsicum/socios-core';
import { useCallback, useRef } from 'react';

import { AccountCreationReasons } from './AccountCreationReasons';
import * as S from './AmlQuestionnaire.styled';
import { Pep } from './Pep';
import { PersonalInformation } from './PersonalInformation';
import { PostalAddress } from './PostalAddress';
import { Profession } from './Profession';
import { SourceOfIncome } from './SourceOfIncome';
import { SourceOfIncomeEstimate } from './SourceOfIncomeEstimate';
import { SpendingEstimate } from './SpendingEstimate';
import { TermsAndConditions } from './TermsAndConditions';

type AmlQuestionnaireProps = {
	isForcedRequirement?: boolean;
	isUniqueRequirement?: boolean;
	onDismiss?: () => void;
	requirement: Requirement<UserAMLQuestionnaireRequirementConfig>;
};

export const AmlQuestionnaire = ({
	isForcedRequirement,
	isUniqueRequirement,
	onDismiss,
	requirement
}: AmlQuestionnaireProps) => {
	const { $t } = useIntl();
	const { handleSubmit, isFirstButtonDisabled, isSubmitLoading, isSuccess, setStepIndex } = useAmlQuestionnaire({
		isUniqueRequirement,
		requirement
	});
	const { showInfoModal } = useInfoModal();

	const stepperRef = useRef<StepperRef>(null);

	const steps = [
		TermsAndConditions,
		AccountCreationReasons,
		() => <PersonalInformation requirement={requirement} />,
		() => <PostalAddress requirement={requirement} />,
		Profession,
		SpendingEstimate,
		SourceOfIncome,
		SourceOfIncomeEstimate,
		Pep
	];

	const onSubmitPress = useCallback(() => {
		showInfoModal({
			buttonOnHandlers: {
				onPrimaryButtonHandler: () => handleSubmit(),
				onSecondaryButtonHandler: () => stepperRef.current?.moveToStepIndex(1)
			},
			buttonTexts: {
				primaryButtonText: $t('global.button.submit'),
				secondaryButtonText: $t('amlQuestionnaire.submitModal.reviewButton')
			},
			image: 'questionnaireSuccess',
			subtitle: $t('amlQuestionnaire.submitModal.description'),
			testID: 'aml-questionnaire-submit-modal',
			title: $t('amlQuestionnaire.submitModal.title'),
			variant: 'confirm'
		});
	}, [handleSubmit, showInfoModal, $t]);

	return (
		<S.StepperWrapper>
			<Stepper
				customBackNavigation={onDismiss}
				disabledFirstButton={isFirstButtonDisabled}
				isLoading={isSubmitLoading || isSuccess}
				onlyButtonsNavigation={isForcedRequirement}
				ref={stepperRef}
				steps={steps}
				testID="aml-questionnaire-stepper"
				onStepIndexChange={setStepIndex}
				onSubmitSteps={onSubmitPress}
			/>
		</S.StepperWrapper>
	);
};
